import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Building2, Users, LayoutGrid, Mail, Phone, Inbox, Scale, ShieldAlert, ListTree } from 'lucide-react'
import { WORKFLOW_ORDER, getWorkflowMeta } from '@/config/workflowModules'
import { searchService, type SearchHit } from '@/services/search'
import { useDealDossierStore, type DealDossierTabInput } from '@/store/dealDossierStore'

/**
 * Global search — live across every record the platform holds.
 *
 * Records (loans, intake, brokers) come from the server so the search
 * reaches beyond whatever the current page happens to have loaded; pages
 * are matched client-side so navigation stays instant. Server results are
 * partition-consistent: search never surfaces a record the pages hide.
 */

const MIN_QUERY = 2

interface ModuleHit {
  id: string
  title: string
  subtitle?: string
  route: string
}

type Group = { key: string; label: string; icon: typeof Search; hits: (SearchHit | ModuleHit)[] }

function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!text) return null
  if (!query || query.length < MIN_QUERY) return <>{text}</>
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <span key={i} className="text-amber-300">{part}</span>
          : <span key={i}>{part}</span>,
      )}
    </>
  )
}

function searchModules(query: string): ModuleHit[] {
  const q = query.toLowerCase().trim()
  if (q.length < MIN_QUERY) return []
  return WORKFLOW_ORDER
    .map((id) => getWorkflowMeta(id))
    .filter((m) => [m.label, m.short, m.description, m.stage].join(' ').toLowerCase().includes(q))
    .slice(0, 4)
    .map((m) => ({ id: m.route, title: m.label, subtitle: m.description, route: m.route }))
}

export default function GlobalSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingEnterRef = useRef<string | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 180)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  const trimmed = debouncedQuery.trim()
  const enabled = trimmed.length >= MIN_QUERY

  const { data, isFetching, isError } = useQuery({
    queryKey: ['global-search', trimmed],
    queryFn: ({ signal }) => searchService.global(trimmed, signal),
    enabled,
    staleTime: 15_000,
    retry: false,
  })

  const modules = useMemo(() => searchModules(trimmed), [trimmed])

  const groups: Group[] = useMemo(() => {
    const out: Group[] = []
    if (data?.loans?.length) out.push({ key: 'loans', label: 'Deals', icon: Building2, hits: data.loans })
    // Plain-English field search: 'cap rate' names the Cap Rate field and the
    // deals that hold it (CRE-18).
    if (data?.fields?.length) out.push({ key: 'fields', label: 'Fields', icon: ListTree, hits: data.fields })
    if (data?.intake?.length) out.push({ key: 'intake', label: 'Intake', icon: Inbox, hits: data.intake })
    if (data?.brokers?.length) out.push({ key: 'brokers', label: 'Brokers & Contacts', icon: Users, hits: data.brokers })
    if (data?.legalMatters?.length) out.push({ key: 'legalMatters', label: 'Legal Matters', icon: Scale, hits: data.legalMatters })
    if (data?.auditFindings?.length) out.push({ key: 'auditFindings', label: 'Audit Findings', icon: ShieldAlert, hits: data.auditFindings })
    if (modules.length) out.push({ key: 'modules', label: 'Pages', icon: LayoutGrid, hits: modules })
    return out
  }, [data, modules])

  const flat = useMemo(() => groups.flatMap((g) => g.hits), [groups])
  const showDropdown = isOpen && enabled
  const hasResults = flat.length > 0

  useEffect(() => { setActiveIndex(-1) }, [trimmed])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Ctrl/Cmd+K focuses search from anywhere.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const openDossier = useDealDossierStore((s) => s.openDossier)
  const go = useCallback((hit: SearchHit | ModuleHit) => {
    setIsOpen(false)
    setQuery('')
    setDebouncedQuery('')
    navigate(hit.route)
    // A field hit on a deal opens that deal's dossier on the page the field
    // is read on — the answer, not the page the answer is somewhere in.
    const field = hit as SearchHit
    if (field.loanId) openDossier(field.loanId, (field.dossierTab ?? 'overview') as DealDossierTabInput)
  }, [navigate, openDossier])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setIsOpen(false)
      inputRef.current?.blur()
      return
    }
    if (e.key === 'Enter' && query.trim().length >= MIN_QUERY && (trimmed !== query.trim() || !hasResults)) {
      // Enter before the answer is in — still debouncing, or still fetching —
      // used to do nothing at all ("typed Magnolia Apartments, nothing
      // happened"). The query is sent now and the first hit opens when it
      // arrives; a search that finds nothing says so in the dropdown.
      e.preventDefault()
      if (debounceRef.current) clearTimeout(debounceRef.current)
      pendingEnterRef.current = query.trim()
      setDebouncedQuery(query)
      setIsOpen(true)
      return
    }
    if (!showDropdown || !hasResults) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((p) => (p < flat.length - 1 ? p + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((p) => (p > 0 ? p - 1 : flat.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = activeIndex >= 0 ? flat[activeIndex] : flat[0]
      if (target) go(target)
    }
  }, [showDropdown, hasResults, flat, activeIndex, go, query, trimmed])

  // An Enter pressed before its answer arrived opens the first hit once the
  // answer for that very query is in (never a stale one for an earlier query).
  useEffect(() => {
    const pending = pendingEnterRef.current
    if (!pending || pending !== trimmed || isFetching || data?.query !== trimmed) return
    pendingEnterRef.current = null
    if (flat.length > 0) go(flat[0])
  }, [data, flat, trimmed, isFetching, go])

  let idx = -1

  return (
    <div ref={containerRef} className="relative flex flex-1 items-center">
      <div className="flex flex-1 items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1 transition-colors focus-within:border-amber-400/30">
        <Search size={12} className="flex-shrink-0 text-stone-600" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true) }}
          onFocus={() => { if (query.trim().length >= MIN_QUERY) setIsOpen(true) }}
          onKeyDown={handleKeyDown}
          placeholder="Search deals, intake, brokers, emails, phone numbers…"
          aria-label="Search everything"
          className="w-full bg-transparent text-[11px] text-stone-300 placeholder:text-stone-600 outline-none"
        />
        {query.length > 0 ? (
          <button
            onClick={() => { setQuery(''); setDebouncedQuery(''); setIsOpen(false); inputRef.current?.focus() }}
            className="flex-shrink-0 text-stone-600 hover:text-stone-300 transition-colors text-[10px]"
          >
            ESC
          </button>
        ) : (
          <kbd className="flex-shrink-0 rounded border border-amber-400/15 bg-black/60 px-1 text-[9px] font-mono text-stone-600">⌘K</kbd>
        )}
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-[100] rounded-lg border border-amber-400/15 bg-[#090909] shadow-2xl overflow-hidden">
          {isError ? (
            <div className="px-3 py-4 text-center text-[11px] text-red-300">
              Search is unavailable right now — check the connection and retry.
            </div>
          ) : !hasResults ? (
            <div className="px-3 py-4 text-center text-[11px] text-stone-600">
              {isFetching
                ? 'Searching…'
                : <>No matches for &ldquo;{trimmed}&rdquo; across deals, fields, intake, brokers, or pages.</>}
            </div>
          ) : (
            <div className="max-h-[440px] overflow-y-auto scrollbar-thin">
              {groups.map((group) => (
                <div key={group.key}>
                  <div className="px-3 py-1.5 flex items-center gap-1.5 border-b border-amber-400/8">
                    <group.icon size={10} className="text-stone-500" />
                    <span className="panel-header-title">{group.label}</span>
                    <span className="ml-auto text-[9px] text-stone-700 font-mono">{group.hits.length}</span>
                  </div>
                  {group.hits.map((hit) => {
                    idx++
                    const myIdx = idx
                    const contact = hit as SearchHit
                    return (
                      <button
                        key={`${group.key}-${hit.id}`}
                        onClick={() => go(hit)}
                        onMouseEnter={() => setActiveIndex(myIdx)}
                        className={`w-full text-left px-3 py-2 flex flex-col gap-0.5 transition-colors cursor-pointer ${
                          activeIndex === myIdx ? 'bg-amber-400/[0.06]' : 'hover:bg-amber-400/[0.04]'
                        }`}
                      >
                        <span className="text-[11px] text-stone-200 font-medium">
                          <HighlightedText text={hit.title} query={trimmed} />
                        </span>
                        {hit.subtitle && (
                          <span className="text-[10px] text-stone-500">
                            <HighlightedText text={hit.subtitle} query={trimmed} />
                          </span>
                        )}
                        {group.key === 'brokers' ? (
                          <span className="flex items-center gap-3 text-[10px] text-stone-500">
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <Mail size={9} className="text-stone-600" />
                                <HighlightedText text={contact.email} query={trimmed} />
                              </span>
                            )}
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone size={9} className="text-stone-600" />
                                <HighlightedText text={contact.phone} query={trimmed} />
                              </span>
                            )}
                          </span>
                        ) : (
                          contact.detail && (
                            <span className="text-[9px] text-stone-600">
                              <HighlightedText text={contact.detail} query={trimmed} />
                            </span>
                          )
                        )}
                      </button>
                    )
                  })}
                </div>
              ))}
              <div className="px-3 py-2 border-t border-amber-400/8 text-center">
                <span className="text-[10px] text-stone-600">
                  <kbd className="inline-block rounded border border-amber-400/15 bg-black/60 px-1 py-0.5 text-[9px] font-mono text-stone-500">↑↓</kbd> navigate
                  {' · '}
                  <kbd className="inline-block rounded border border-amber-400/15 bg-black/60 px-1 py-0.5 text-[9px] font-mono text-stone-500">Enter</kbd> open
                  {' · '}
                  <kbd className="inline-block rounded border border-amber-400/15 bg-black/60 px-1 py-0.5 text-[9px] font-mono text-stone-500">Esc</kbd> close
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
